"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, MessageSquare, PhoneCall, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const benefits = [{
  title: "Application Feasibility & Method Assessment",
  description: "Evaluate your sample matrices, measurement goals, and regulatory requirements before selecting technology."
}, {
  title: "Tailored Instrumentation & PAT Integration",
  description: "Guidance on NIR, Raman, chromatography, and physical testing systems engineered for inline or lab deployment."
}, {
  title: "Lifecycle Support & Qualification Services",
  description: "Direct access to field specialists for IQ/OQ compliance, routine calibration, and ongoing method optimization."
}];
export default function DiscussApplication() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="discussapplicationcta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-secondary/60 via-card to-background p-8 md:p-12 lg:p-16 shadow-lg">
          {/* Subtle Decorative Background Accents */}
          <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 -bottom-20 size-80 rounded-full bg-secondary/80 blur-2xl" />

          <motion.div className="relative z-10 grid gap-10 lg:grid-cols-12 lg:items-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants}>
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <motion.div variants={itemVariants} className="flex items-center gap-2">
                <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium">
                  <FlaskConical className="size-3.5 text-primary" />
                  Application Consultation
                </Badge>
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  Regulated Lab & Process Specialists
                </span>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-3">
                <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl leading-tight">
                  Discuss Your Analytical Application With Our Technical Team
                </h2>
                <p className="font-sans text-base text-muted-foreground md:text-lg leading-relaxed">
                  Connect directly with experienced instrumentation specialists across Ireland and the UK. We assess feasibility, match suitable analytical technologies, and plan seamless compliance for your laboratory or production line.
                </p>
              </motion.div>

              {/* Action Buttons */}
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="shadow-md">
                  <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                    <MessageSquare className="size-4" />
                    <span>Discuss Your Application</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Contact Direct</span>
                  </Link>
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-6 pt-4 text-xs text-muted-foreground border-t border-border/70">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>GMP & ISO Compliant Methodologies</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" />
                  <span>Confidential Technical Reviews</span>
                </div>
              </motion.div>
            </div>

            {/* Right Feature Panel */}
            <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-4 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
              <h3 className="font-default text-lg font-semibold text-card-foreground">
                What to expect during consultation:
              </h3>

              <div className="space-y-4">
                {benefits.map((benefit, index) => <div key={index} data-index={index} className="flex items-start gap-3.5 rounded-lg p-2.5 transition-colors hover:bg-muted/50">
                    <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <span className="text-xs font-bold">{index + 1}</span>
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-default text-sm font-semibold text-foreground">
                        {benefit.title}
                      </h4>
                      <p className="font-sans text-xs text-muted-foreground leading-normal">
                        {benefit.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="mt-2 rounded-lg bg-secondary p-3.5 text-xs text-secondary-foreground flex items-center justify-between">
                <span>Need immediate feasibility testing?</span>
                <Link to="/FeasibilityStudiesPage" className="font-semibold text-primary hover:underline ml-2 whitespace-nowrap">
                  Explore Feasibility Studies
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}